import classnames from 'classnames';
import { flow, isEqual, unset } from 'lodash/fp';
import React, { useCallback } from 'react';
import Modal from 'react-modal';
import { RawDraftContentState } from 'draft-js';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import { CustomTooltip, InformationTooltip, TooltipList } from '../../shared/tooltip';
import { AgencyLinkedInstanceField, AnnexInstanceField, closeAnnexFieldWizard, CurrencyValue, FieldValue, getWizardFieldOpen, openAnnexFieldWizard, OpenFieldSection, SingleInstanceField } from '../instances/store';
import styles from './Fields.module.scss';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { CalculatorField } from '../../admin/calculator/store';
import { DatasetFieldType } from '../store';
import { CurrencyAmountField } from './CurrencyAmountField';
import { DateField } from './DateField';
import { DocumentField } from './DocumentField';
import { LabelField } from './LabelField';
import { PartySelect } from './PartySelect';
import { WYSIWYGField } from './WYSIWYGField';
import { Text } from './Text';
import { LongText } from './LongText';
import { Number } from './Number';
import { Checkbox } from './Checkbox';
import { Time } from './Time';
import { CalculatorField as Calculator } from './CalculatorField';
import { Form } from '../../shared/icons';
import { Dropdown } from './Dropdown';

const getField = (
    field: SingleInstanceField,
    value: FieldValue,
    showFieldUpdated: boolean,
    showDatasetUpdated: boolean,
    showAIModified: boolean,
    showAIModifiedUserCorrected: boolean,
    includedInAnnex: boolean,
    index: number,
    disabled: boolean,
    fieldOpen: boolean,
    datasetId: number,
    parentFieldId: string,
    toggleSection: (fieldSection: OpenFieldSection) => void,
    getSectionOpen: (fieldSection: OpenFieldSection) => boolean,
    sectionId?: string,
    groupIndex?: number,
    menuPortalTarget?: HTMLElement | null,
    modalInstance?: boolean
) => {
    const defaultProps = { id: field.id!, index, sectionId, groupIndex, disabled, showFieldUpdated, showDatasetUpdated, showAIModified, showAIModifiedUserCorrected, includedInAnnex, modalInstance, parentFieldId };
    switch (field.type) {
        case DatasetFieldType.TEXT:
            return <Text {...defaultProps} value={value as string} datasetId={datasetId} />;
        case DatasetFieldType.LONGTEXT:
            return <LongText {...defaultProps} value={value as string} datasetId={datasetId} />;
        case DatasetFieldType.DATE:
            return <DateField {...defaultProps} value={value as null | Date} datasetId={datasetId} />;
        case DatasetFieldType.DROPDOWN:
        case DatasetFieldType.LINKFIELD:
            return <Dropdown
                {...defaultProps}
                value={value as null | string[]}
                type={field.type}
                listId={field.settings.dropdownLinked!}
                isMulti={field.settings.isMultiSelect}
                entityType={field.settings.entityType}
                menuPortalTarget={menuPortalTarget}
                datasetId={datasetId}
            />;
        case DatasetFieldType.PARTY:
            return <PartySelect
                {...defaultProps}
                value={value as null | string[]}
                menuPortalTarget={menuPortalTarget}
                datasetId={datasetId}
            />;
        case DatasetFieldType.DOCUMENT:
            return <DocumentField id={field.id!} documentProperty={field.settings.documentProperty!} />;
        case DatasetFieldType.NUMBER:
            return <Number {...defaultProps} value={value as number} datasetId={datasetId} />;
        case DatasetFieldType.CHECKBOX:
            return <Checkbox {...defaultProps} checked={value as boolean} datasetId={datasetId} centreField={field.settings.centreField} />;
        case DatasetFieldType.TIME:
            return <Time {...defaultProps} value={value as string[]} datasetId={datasetId} />;
        case DatasetFieldType.WYSIWYG:
            return <WYSIWYGField
                {...defaultProps}
                value={value as RawDraftContentState | null}
                fieldOpen={fieldOpen}
                toolbarHidden={disabled}
                datasetId={datasetId}
                isCollapsible={field.settings.isCollapsible}
                toggleSection={toggleSection}
                getSectionOpen={getSectionOpen}
            />;
        case DatasetFieldType.LABEL:
            return <LabelField value={field.settings.labelContent!} />;
        case DatasetFieldType.CALCULATOR:
            return <Calculator
                {...defaultProps}
                value={value as CalculatorField[]}
                fieldType={field.settings.calculatorFieldType!}
                showOutputField={field.settings.showOutputField!}
                showGracePeriod={field.settings.showGracePeriod!}
                includeFreeTextOption={field.settings.includeFreeTextOption}
                fieldOpen={fieldOpen}
                datasetId={datasetId}
            />;
        case DatasetFieldType.CURRENCY_AMOUNT:
            return <CurrencyAmountField
                {...defaultProps}
                currencyValue={value as CurrencyValue}
                datasetId={datasetId}
            />;
        default:
            return <div className={styles.unknownFieldType}>Field type unknown.</div>;
    }
};

interface WizardProps {
    id: string;
    value: string[];
    index: number;
    disabled?: boolean;
    rowId: string;
    showFieldUpdated?: boolean;
    showDatasetUpdated?: boolean;
    showAIModified?: boolean;
    field: AnnexInstanceField;
    isPreview?: boolean;
    datasetId: number;
    parentFieldId: string;
    toggleSection: (fieldSection: OpenFieldSection) => void;
    getSectionOpen: (fieldSection: OpenFieldSection) => boolean;
}

export const Wizard: React.FC<WizardProps> = ({
    id,
    disabled = false,
    index,
    rowId,
    showFieldUpdated = false,
    showDatasetUpdated = false,
    field,
    value,
    isPreview = false,
    datasetId,
    parentFieldId,
    toggleSection,
    getSectionOpen
}) => {
    const dispatch = useAppDispatch();
    const [screenWidth, screenHeight] = useWindowResize();
    const wizardFieldOpen = useAppSelector(getWizardFieldOpen);
    const openWizard = () => dispatch(openAnnexFieldWizard(index, rowId));
    const closeWizard = () => dispatch(closeAnnexFieldWizard());
    const isOpen = isEqual({ rowId, index }, wizardFieldOpen);

    const stripField = useCallback((linkedField: AgencyLinkedInstanceField): SingleInstanceField => flow(
        unset('datasetTitle'),
        unset('datasetId')
    )(linkedField), []);

    return (
        <div className={styles.wizardField} data-testid={`annex-definition-wizard-${id}`}>
            <button
                onClick={openWizard}
                className={classnames(styles.wizardButton, {
                    [styles.updatedWizardButton]: showFieldUpdated,
                    [styles.newWizardButton]: showDatasetUpdated
                })}
                disabled={isPreview}
            >
                Open Wizard
            </button>
            <TooltipList overlayText={value}>
                <div className={styles.informationTooltipIcon}>i</div>
            </TooltipList>
            <Modal
                isOpen={isOpen}
                className={styles.wizardModal}
                ariaHideApp={false}
                shouldCloseOnOverlayClick
                shouldCloseOnEsc
                onRequestClose={closeWizard}
                style={{
                    overlay: {
                        display: 'flex',
                        backgroundColor: 'transparent'
                    },
                    content: {
                        position: 'absolute',
                        top: `${screenHeight / 4}px`,
                        left: `${(screenWidth / 2) + 30}px`,
                        overflow: 'visible',
                        outline: 'none'
                    }
                }}
            >
                <div className={styles.wizardWrapper}>
                    <ModalHeader label={field.aliasLabel || field.label} icon={Form} />
                    <div className={styles.fieldsWrapper}>
                        <Scrollable>
                            {field.linkedFields.map((field, wizardFieldIndex) => {
                                const { label, description, refLabel, settings, value } = field;
                                const strippedField = stripField(field);
                                return (
                                    <div
                                        className={styles.singleFieldWrapper}
                                        data-testid={`wizard-field-${wizardFieldIndex}-${id}-wrapper`}
                                        key={field.id!}
                                    >
                                        <div className={styles.fieldTitleWrapper}>
                                            <CustomTooltip overlayText={label} trigger='click'>
                                                <div className={classnames(styles.fieldLabel, { [styles.emptyFieldLabel]: !label })}
                                                    data-testid={`form-field-${wizardFieldIndex}-${id}-label`}>
                                                    {label || 'Label...'}
                                                </div>
                                            </CustomTooltip>
                                            {description && <InformationTooltip content={description} />}
                                            {refLabel && settings.showRef && <InformationTooltip content={refLabel} label='?' />}
                                        </div>
                                        {getField(strippedField, value, false, false, false, false, false, wizardFieldIndex, disabled, false, datasetId, parentFieldId, toggleSection, getSectionOpen, undefined, undefined, document.body)}
                                    </div>
                                );
                            })}
                        </Scrollable>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
